import { useState } from "react";
import type { SearchCounts } from "@/lib/api/catalogue";
import { actionProps } from "@/lib/controlAnalytics";
import { clearedFilters, hasSearchFilters, type JobsStatus, type JobsView } from "@/lib/jobs";
import JobsDistanceFilter from "./JobsDistanceFilter";
import {
    LanguageFilter,
    PostedFilter,
    SalaryFilter,
    WorkSettingFilter,
    type FilterToggle,
} from "./JobsFilterMenus";
import JobsStatusTabs from "./JobsStatusTabs";

type FilterName = "work" | "posted" | "distance" | "salary" | "language";

/**
 * The Jobs filters, two lines over the results: the status tabs for a member,
 * then a dropdown each for how, when, how far, pay and language, with Clear
 * filters at the far end once any is on. The buttons wrap on a narrow screen
 * and the line never scrolls, which would clip their panels. One panel is open
 * at a time.
 */
export default function JobsFilters({
    view,
    member,
    statusCounts,
    envoyCounts = false,
    counts,
    nowhere = false,
    onChange,
}: {
    view: JobsView;
    member: boolean;
    /** Each tab's count, where it has one; see `tabCounts`. */
    statusCounts: Partial<Record<JobsStatus, number>> | undefined;
    /** In queue and Applied count envoy's totals, not this page. */
    envoyCounts?: boolean;
    counts: SearchCounts | undefined;
    /** The catalogue could not tell where the person is, so no distance holds. */
    nowhere?: boolean;
    onChange: (patch: Partial<JobsView>) => void;
}) {
    const [opened, setOpened] = useState<FilterName | null>(null);
    const toggle = (name: FilterName): FilterToggle => ({
        open: opened === name,
        onOpenChange: (open) =>
            setOpened((current) => (open ? name : current === name ? null : current)),
    });
    return (
        <div className="flex min-w-0 flex-col gap-3.5">
            {member && (
                <JobsStatusTabs
                    status={view.status}
                    counts={statusCounts}
                    envoyCounts={envoyCounts}
                    onStatus={(status) => onChange({ status })}
                />
            )}
            <div role="group" aria-label="Filters" className="flex flex-wrap items-center gap-2">
                <WorkSettingFilter
                    view={view}
                    counts={counts}
                    toggle={toggle("work")}
                    onChange={onChange}
                />
                <PostedFilter view={view} toggle={toggle("posted")} onChange={onChange} />
                <JobsDistanceFilter
                    within={view.within}
                    nowhere={nowhere}
                    toggle={toggle("distance")}
                    onChange={onChange}
                />
                <SalaryFilter view={view} toggle={toggle("salary")} onChange={onChange} />
                <LanguageFilter view={view} toggle={toggle("language")} onChange={onChange} />
                {hasSearchFilters(view) && (
                    <button
                        {...actionProps("job_filters_cleared", { source: "Jobs" })}
                        type="button"
                        onClick={() => onChange(clearedFilters())}
                        className="ml-auto text-[13px] font-medium whitespace-nowrap text-brand-ink underline underline-offset-2 max-[760px]:inline-flex max-[760px]:min-h-11 max-[760px]:items-center"
                    >
                        Clear filters
                    </button>
                )}
            </div>
        </div>
    );
}
